import moment from 'moment-timezone';
import base64 from 'base-64';
import axios from 'axios';
import { apiUrl, timeout, dayMap, GEOLOCATION_PERMISSION, GEOLOCATION_ASK_TIMEOUT } from './constants';
import { getGeocode, getLatLng } from 'use-places-autocomplete';
import 'moment/locale/id'; 

moment.locale('id');
moment.tz.setDefault('Asia/Jakarta');

export const formatDate = (value, format = 'DD-MM-YYYY H:m', isUTC = false, timeZone = 'Asia/Jakarta') => {
	// Jika tidak ada value, kembalikan string kosong
	if (!value) return '';
	// console.log('value: ', value);

	// Buat objek tanggal menggunakan moment langsung dari value
	let date;

	if (typeof value === 'string') {
		// Jika value sudah dalam format ISO, langsung gunakan moment
		date = moment(value, moment.ISO_8601, true); // True untuk strict parsing
	} else {
		// Jika value bukan string, cek apakah ini valid date object
		date = moment(value);
	}

	// console.log('Moment Date: ', date.isValid() ? date.format() : 'Invalid Date');

	// Periksa apakah date valid
	if (!moment(date).isValid()) {
		// console.log('date: ', date);
		return 'not valid'; // Kembalikan string kosong jika tanggal tidak valid
	}

	// Jika isUTC adalah true, konversi dari UTC ke timezone Jakarta
	if (isUTC) {
		return moment.utc(date).tz(timeZone).format(format);
	} else {
		// Jika tidak, format tanggal langsung dengan timezone yang diberikan
		return moment(date).tz(timeZone).format(format);
	}
};

export const formatPhoneNumber = (phone = '') => {
	if (!phone) return '';
	// If the phone number starts with '+62', return as is
	if (phone.startsWith('+62')) {
		return phone;
	}
	// If the phone number starts with '0', replace the first '0' with '+62'
	if (phone.startsWith('0')) {
		return `+62${phone.slice(1)}`;
	}
	// If the phone number starts with '62', prepend '+'
	if (phone.startsWith('62')) {
		return `+${phone}`;
	}
	// Otherwise, return the phone number as is
	return phone;
};

export const formatPhoneNumberWa = (phone = '') => {
	if (!phone) return '';
	// If the phone number starts with '+62', return as is
	if (phone.startsWith('+62')) {
		return phone.replace('+', '');
	}
	// If the phone number starts with '0', replace the first '0' with '+62'
	if (phone.startsWith('0')) {
		return `62${phone.slice(1)}`;
	}
	// If the phone number starts with '62', prepend '+'
	if (phone.startsWith('62')) {
		return `${phone}`;
	}
	// Otherwise, return the phone number as is
	return phone;
};

export const addCountryCode = (phone = '') => {
	if (!phone || phone === '+') return '';

	// Trim any whitespace and ensure the phone doesn't contain extra '+' signs
	phone = phone.trim();

	// If the phone number already starts with '+62', return it as is
	if (phone.startsWith('+62')) {
		return phone;
	}

	// If the phone number starts with '0', replace the first '0' with '+62'
	if (phone.startsWith('0')) {
		const result = `+62${phone.slice(1)}`;
		return result;
	}

	// If the phone number starts with '+6', treat it as +62
	if (phone.startsWith('+6')) {
		const result = `+62${phone.slice(2)}`;
		return result;
	}

	// If the phone number starts with '62', just prepend '+'
	if (phone.startsWith('62')) {
		const result = `+${phone}`;
		return result;
	}

	// Otherwise, assume it's a local number and add '+62'
	const result = `+62${phone}`;
	return result;
};

export const formatLocalPhoneNumber = (phone = '') => {
	if (!phone) return '';

	phone = phone.trim().replace(/\s+/g, ''); // bersihkan spasi

	// Jika nomor dimulai dengan '+62', ganti dengan '0'
	if (phone.startsWith('+62')) {
		return `0${phone.slice(3)}`;
	}

	// Jika nomor dimulai dengan '62', ganti dengan '0'
	if (phone.startsWith('62')) {
		return `0${phone.slice(2)}`;
	}

	// Jika sudah dimulai dengan '0', biarkan
	if (phone.startsWith('0')) {
		return phone;
	}

	// Jika tidak cocok format manapun, anggap lokal
	return `0${phone}`;
};

export const formatRupiah = (value, { showCurrency = true, fractionDigits = 0 } = {}) => {
	const formatter = new Intl.NumberFormat('id-ID', {
		style: showCurrency ? 'currency' : 'decimal',
		currency: 'IDR',
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	});

	return showCurrency
		? formatter.format(value) // e.g. "Rp25.000"
		: formatter
				.format(value)
				.replace(/[Rp\s]/g, '')
				.trim(); // e.g. "25.000"
};

export function MathRandom(min, max) {
	return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const saveToIndexedDB = (key, imageUrl, dataUrl) => {
	const dbName = key;
	const request = indexedDB.open(dbName, 1);

	request.onupgradeneeded = (event) => {
		const db = event.target.result;
		db.createObjectStore('images', { keyPath: 'url' });
	};

	request.onsuccess = (event) => {
		const db = event.target.result;
		const transaction = db.transaction(['images'], 'readwrite');
		const store = transaction.objectStore('images');
		store.put({ url: imageUrl, data: dataUrl });

		transaction.oncomplete = () => {
			console.log('Image saved to IndexedDB');
		};
	};

	request.onerror = (event) => {
		console.error('Error opening IndexedDB:', event.target.error);
	};
};

export const getFromIndexedDB = (key, url) => {
	return new Promise((resolve, reject) => {
		const dbName = key;
		const request = indexedDB.open(dbName, 1);

		request.onupgradeneeded = (event) => {
			const db = event.target.result;
			db.createObjectStore('images', { keyPath: 'url' });
		};

		request.onsuccess = (event) => {
			const db = event.target.result;
			const transaction = db.transaction(['images'], 'readonly');
			const store = transaction.objectStore('images');
			const getRequest = store.get(url);

			getRequest.onsuccess = () => {
				const result = getRequest.result;
				resolve(result ? result.data : null);
			};

			getRequest.onerror = (event) => {
				reject(event.target.error);
			};
		};

		request.onerror = (event) => {
			reject(event.target.error);
		};
	});
};

export const displayImageFromIndexedDB = async (key, imageUrl) => {
	try {
		const dataUrl = await getFromIndexedDB(key, imageUrl);
		return dataUrl || null;
	} catch (error) {
		console.error('Error fetching image from IndexedDB:', error);
		return null;
	}
};

export const fetchAndCompressImage = async (key, product_thumbnail, imageUrl) => {
	let dataUrl = await getFromIndexedDB(key, product_thumbnail);

	if (!dataUrl) {
		try {
			const response = await fetch(imageUrl, {
				method: 'GET',
				redirect: 'follow',
			});

			if (response.ok) {
				const blob = await response.blob();
				const reader = new FileReader();
				reader.onloadend = () => {
					dataUrl = reader.result;
					saveToIndexedDB(key, product_thumbnail, dataUrl);
				};
				reader.readAsDataURL(blob);
			} else {
				console.error('Error fetching image:', response.statusText);
			}
		} catch (error) {
			console.error('Error in fetchAndCompressImage:', error);
		}
	}

	return dataUrl;
};

export const isEmptyData = (value) => {
	return (
		value === null ||
		value === undefined ||
		(typeof value === 'string' && (value === 'undefined' || value === 'null' || value.trim() === '')) ||
		(Array.isArray(value) && value.length === 0) ||
		(typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
	);
};

export const encodeValue = (value) => {
	if (isEmptyData(value)) return null;
	return base64.encode(value);
};

export const encodeValueURL = (value) => {
	if (isEmptyData(value)) return null;
	return base64.encode(value);
};

export const microtime = (getAsFloat = false) => {
	const now = Date.now() / 1000;
	const seconds = Math.floor(now);
	const microseconds = Math.round((now - seconds) * 1e6);

	if (getAsFloat) {
		return now;
	}

	return `${microseconds}_${seconds}`;
};

export const axiosInstance = axios.create({
	baseURL: apiUrl,
	timeout: timeout,
});

let userStore = null;

export const injectStore = (_store) => {;
  userStore = _store;
};

export const authHeaders = () => {
	const { token, is_b2b: isB2b } = userStore?.getState()?.user || {};

	const headers = {};

	if (!isEmptyData(token)) {
		headers.Authorization = `Bearer ${token}`;
	}

	headers['X-B2B'] = isB2b;

	return { headers };
};

export const sendAnalyticsEvent = (categoryName = 'Custom', eventName = '', value = '') => {
	if (typeof window.gtag === 'function') {
		// console.log("sendAnalyticsEvent:", { categoryName, eventName, value });

		window.gtag('event', eventName, {
			event_category: categoryName,
			event_label: eventName,
			value: value,
		});
	}
};

export const sendAnalyticsCustomEvent = (eventName = '', events = {}) => {
	if (typeof window.gtag === 'function') {
		// console.log("sendAnalyticsCustomEvent:", eventName, events);

		window.gtag('event', eventName, events);
	}
};

export const sendAnalyticsPageView = (pageName) => {
	if (typeof window.gtag === 'function') {
		// console.log("sendAnalyticsPageView:", pageName);

		window.gtag('event', 'page_view', {
			page_title: pageName,
			page_path: window.location.pathname,
		});
	}
};

export const hexToRgb = (hex) => {
	const bigint = parseInt(hex.replace('#', ''), 16);
	return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255].join(', ');
};

export const checkTime = (time) => {
	if (time !== '' && time !== null) {
		var timeSplit = time.split(':');
		var merid = 'AM';
		if (timeSplit[0] >= 12) {
			merid = 'PM';
		}
		return merid;
	}
};

export const checkHoldTime = (endHold) => {
	if (!endHold) return false;
	const now = new Date();
	const holdTime = new Date(endHold);

	return now <= holdTime;
};

export const decodeValue = function (value) {
	if (isEmptyData(value)) return null;
	return base64.decode(value);
};

export const decodeAtob = (value) => {
	if (isEmptyData(value)) return null;
	return atob(value);
};

export const removeHTML = (html) => {
	return html.replace(/<\/?[^>]+(>|$)/g, '');
};

export const classifyPhoneNumber = (input = '') => {
	if (!input) return { type: 'invalid', normalized: '', possibleWhatsapp: false };

	// Hapus karakter non-digit
	let cleaned = input.replace(/\D/g, '');

	// Konversi +62 atau 62 ke 0
	if (cleaned.startsWith('62')) {
		cleaned = '0' + cleaned.slice(2);
	}

	// Tambahkan 0 jika langsung dimulai dengan 8
	if (cleaned.startsWith('8')) {
		cleaned = '0' + cleaned;
	}

	// Validasi nomor HP lokal Indonesia: 08xxxxxxxxx (10-13 digit)
	const isValidMobile = /^08[1-9][0-9]{7,10}$/.test(cleaned);

	if (isValidMobile) {
		return {
			type: 'mobile',
			normalized: cleaned,
			possibleWhatsapp: true,
		};
	}

	return {
		type: 'invalid',
		normalized: '',
		possibleWhatsapp: false,
	};
};

export const numberOnly = (value) => {
	return value.replace(/[^0-9]/g, '');
};

export const handleFormatingAddress = ({ lat = null, lng = null, formatted_address = null, address_components = null, detail = null }) => {
	if (!lat || !lng || !formatted_address || !address_components?.length) return;

	let country = '';
	let state = '';
	let city = '';
	let village = '';
	let district = '';
	let postalCode = '';

	address_components?.forEach((item) => {
		if (item.types.includes('country')) {
			country = item.long_name;
		}
		if (item.types.includes('administrative_area_level_1')) {
			state = item.long_name;
		}
		if (item.types.includes('administrative_area_level_2')) {
			city = item.long_name;
		}
		if (item.types.includes('administrative_area_level_3')) {
			village = item.long_name;
		}
		if (item.types.includes('administrative_area_level_4')) {
			district = item.long_name;
		}
		if (item.types.includes('postal_code')) {
			postalCode = item.long_name;
		}
	});
	const formatted = {
		address: removeCodeFromAddress(formatted_address),
		name: district || village,
		lat,
		lng,
		country,
		state,
		city,
		village,
		district,
		postalCode,
		detail,
	};
	return formatted;
};

export const getDetailLocationFromLatLong = async (latitude = null, longitude = null, place_id = null) => {
  console.log('getDetailLocationFromLatLong');
	try {
		let retries = 0;
		while (
		(!window.google ||
			!window.google.maps ||
			typeof window.google.maps.Geocoder !== 'function') &&
			retries < 5
		) {
			console.warn(`Google Maps belum siap (percobaan ${retries + 1})...`);
			await new Promise((resolve) => setTimeout(resolve, 500));
			retries++;
		}

		if (
			!window.google ||
			!window.google.maps ||
			typeof window.google.maps.Geocoder !== 'function'
		) {
			throw new Error('Google Maps masih belum siap setelah beberapa kali percobaan');
		}

		if (!isEmptyData(place_id)) {
			const results = await getGeocode({ placeId: place_id });
			if (!results || results.length === 0) {
				console.warn('No results from place_id geocode');
				return null;
			}

			const result = results[0];

			if (isEmptyData(latitude) || isEmptyData(longitude)) {
				const { lat, lng } = getLatLng(result);
				latitude = lat;
				longitude = lng;
			}

			return handleFormatingAddress({
				lat: latitude,
				lng: longitude,
				formatted_address: result?.formatted_address,
				address_components: result?.address_components,
				detail: null,
			});
		}
		if (!isEmptyData(latitude) && !isEmptyData(longitude)) {
			const results = await getGeocode({ location: { lat: Number(latitude), lng: Number(longitude) } });
			if (!results || results.length === 0) {
				console.warn('No results from lat/lng geocode');
				return null;
			}

			const result = results[0];
			return handleFormatingAddress({
				lat: latitude,
				lng: longitude,
				formatted_address: result?.formatted_address,
				address_components: result?.address_components,
				detail: null,
			});
		}

		console.warn('No valid input for geocoding');
		return null;
	} catch (error) {
		console.error('Error fetching location details:', error);
		return null;
	}
};


export const getCurrentLocation = async () => {
	return new Promise((resolve, reject) => {
		if (!navigator.geolocation) {
			console.log('Geolocation is not supported by this browser.');
			return resolve(null);
		}

		navigator.geolocation.getCurrentPosition(
			(position) => {
				const coords = position?.coords;
				const isValidCoords = coords && typeof coords.latitude === 'number' && typeof coords.longitude === 'number';

				if (isValidCoords) {
					resolve(coords);
				} else {
					console.warn('Received invalid coords:', coords);
					resolve(null); // Jangan teruskan coords kosong
				}
			},
			(error) => {
				let errorMessage;
				switch (error.code) {
					case error.PERMISSION_DENIED:
						errorMessage = 'Pengguna tidak mengizinkan akses ke lokasi.';
						break;
					case error.POSITION_UNAVAILABLE:
						errorMessage = 'Informasi lokasi tidak tersedia.';
						break;
					case error.TIMEOUT:
						errorMessage = 'Permintaan untuk mendapatkan lokasi pengguna kedaluwarsa.';
						break;
					case error.UNKNOWN_ERROR:
						errorMessage = 'Terjadi kesalahan yang tidak diketahui.';
						break;
					default:
						errorMessage = 'Terjadi kesalahan yang tidak terduga.';
				}
				console.log('errorMessage: ', errorMessage);
				resolve(null); // Tetap resolve null agar bisa lanjut retry
			},
			{
				enableHighAccuracy: true,
				timeout: 10000,
				maximumAge: 0,
			},
		);
	});
};

export const detectPlatformInfo = (userAgent) => {
	const ua = userAgent.toLowerCase();

	const isAndroid = /android/.test(ua);
	const isIOS = /iphone|ipad|ipod/.test(ua);
	const isChrome = /chrome|crios/.test(ua) && !/edge|opr|opera/.test(ua);
	const isSafari = /safari/.test(ua) && !/chrome|crios|android/.test(ua);

	const isWebView =
		/(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(userAgent) || // iOS WebView
		/; wv\)/.test(userAgent) || // Android WebView (common)
		/\bwv\b/.test(ua); // Alternative Android WebView

	return {
		isAndroid,
		isIOS,
		isWebView,
		isChrome,
		isSafari,
	};
};

export const removeCodeFromAddress = (address = "") => {
  return address.replace(/^[A-Z0-9]+(\+[A-Z0-9]+)+,?\s*/, "").trim();
};

export const isEmptyPrice = (value) => {
  const num = Number(value);
  return isNaN(num) || num <= 0;
}

export const checkScope = (scope, value) => {
  let list = [];

  if (Array.isArray(scope)) {
    list = scope;
  } else if (typeof scope === "string") {
    if (!scope || scope === "null" || scope === "undefined") {
      list = [];
    } else {
      list = scope.split(",");
    }
  } else if (!scope) {
    list = [];
  }

  list = list
    .map(x => x?.toString?.().trim())
    .filter(x => x !== "" && x !== "null" && x !== "undefined");

  if (list.length === 0) {
    return true;
  };
  return list.includes(String(value));
};

export const initMetaPixel = (metaPixelId) => {
	if (!metaPixelId) {
		console.warn('Meta Pixel is Required!');
		return;
	}

  if (typeof window === 'undefined') return;

  if (window.fbq && window.fbq.loaded) {
    console.log('Meta Pixel already initialized!');
    return;
  }

  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', metaPixelId);
  window.fbq('track', 'PageView');
};

export const trackMetaPixelEvent = (eventName, params = null) => {
	// console.log('trackMetaPixelEvent', eventName, params);
	if (window.fbq) {
		if(params){
			window.fbq('trackCustom', eventName, params);
			return;
		}
		window.fbq('trackCustom', eventName);
	} else {
		console.warn('fbq not initialized!');
	}
};

export function getSecondsLeft(unixtime) {
  if (!unixtime) return 0;

  const expire = moment.unix(unixtime).tz("Asia/Jakarta");
  const now = moment().tz("Asia/Jakarta");
  const diffSeconds = expire.diff(now, "seconds");
  return Math.max(0, diffSeconds);
}

export const formatTime = (seconds) => {
  const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
  const secs = String(seconds % 60).padStart(2, '0');
  return `00:${minutes}:${secs}`;
};

export function unixToDate(unixtime, format = "YYYY-MM-DD HH:mm:ss") {
  if (!unixtime) return null;

  return moment.unix(unixtime).tz("Asia/Jakarta").format(format);
}

export function dateToUnix(date) {
  if (!date) return null;

  const utcTime = moment.utc(date, "YYYY-MM-DD HH:mm:ss", true);

  if (!utcTime.isValid()) return null;

  return utcTime.tz("Asia/Jakarta").unix();
}


export const downloadImage = async (imageUrl, name = 'download') => {
  if (!imageUrl) throw new Error('URL gambar tidak ditemukan');

  const fileName = name.endsWith('.png') ? name : `${name}.png`;
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isBlobUrl = imageUrl.startsWith('blob:');

  if (!isBlobUrl && !(isIOS || isSafari)) {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    link.remove();
    return;
  }

  const response = await fetch(imageUrl, { mode: 'cors' });
  if (!response.ok) throw new Error('Gagal mengambil gambar');

  const blob = await response.blob();
  if ((isIOS || isSafari) && navigator.share) {
    const file = new File([blob], fileName, { type: 'image/png' });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: name,
      });
      return;
    }

    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    throw new Error('MANUAL_SAVE');
  }

  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const generateUUID = () => {
  return crypto.randomUUID();
};

export const sha256 = async (text) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
};

export const generateRandomFingerprint = async () => {
  const uuid = generateUUID();
  const fingerprint = await sha256(uuid);
  return fingerprint;
};

export const sortVouchers = (vouchers = []) => {
  return vouchers.sort((a, b) => {
    // 1️⃣ Group disabled ke paling bawah
    if (a.disabled !== b.disabled) {
      return a.disabled ? 1 : -1;
    }

    // Jika keduanya disabled → tidak perlu lanjut sorting
    if (a.disabled && b.disabled) return 0;

    // 2️⃣ Prioritas: day_time lebih tinggi dari date_range
    const aDayTime = a.periode_type === "day_time";
    const bDayTime = b.periode_type === "day_time";

    if (aDayTime !== bDayTime) {
      return aDayTime ? -1 : 1; // day_time lebih dulu
    }

    // 3️⃣ Jika dua-duanya date_range → urutkan berdasarkan end_date terdekat
    const aHasDate = a.periode_type === "date_range" && a.end_date;
    const bHasDate = b.periode_type === "date_range" && b.end_date;

    if (aHasDate && bHasDate) {
      return moment(a.end_date).valueOf() - moment(b.end_date).valueOf();
    }

    // 4️⃣ Jika salah satu punya date_range tapi kosong value → date_range tetap prioritas
    if (aHasDate !== bHasDate) {
      return aHasDate ? -1 : 1;
    }

    // 5️⃣ Jika dua-duanya day_time atau tanpa rules → anggap sama
    return 0;
  });
};

export const calculateAddonPackagingFee = (addonPackagingFee, minAddonPackagingFee, totalItem) => {
	return addonPackagingFee * Math.floor(totalItem / minAddonPackagingFee)
}

export const initGoogleAnalytics = (measurementId) => {
  if (isEmptyData(measurementId)) return;
	if (window.__gaInitialized) return;
	window.__gaInitialized = true;

	const script = document.createElement("script");
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
	document.head.appendChild(script);

	window.dataLayer = window.dataLayer || [];
	window.gtag = function(){ window.dataLayer.push(arguments); };

	window.gtag('js', new Date());
	window.gtag('config', measurementId);
};

export const formatRupiahToK = (value = 0) => {
  if (value >= 1_000_000) return `${Math.floor(value / 1_000_000)}M`;
  if (value >= 1_000) return `${Math.floor(value / 1_000)}K`;
  return String(value);
};

export const formatOperationalHour = (operational) => {
  if (!operational) return [];

  const now = new Date();
  const todayIndex = now.getDay(); // 0 = Minggu
  const todayKey = dayMap[todayIndex].key;

  const currentTime = now.getHours() * 60 + now.getMinutes();

  return Object.values(dayMap).map((day) => {
    const open = operational[`${day.key}_open_time`];
    const close = operational[`${day.key}_close_time`];

    let isActive = false;

    if (day.key === todayKey && open && close) {
      const [openH, openM] = open.split(":").map(Number);
      const [closeH, closeM] = close.split(":").map(Number);

      const openTime = openH * 60 + openM;
      const closeTime = closeH * 60 + closeM;

      isActive = currentTime >= openTime && currentTime <= closeTime;
    }

    return {
      hari: day.label,
      jam: `${open} - ${close}`,
      isActive,
    };
  });
};

export const cleanText = (text) =>
	text
		?.toLowerCase()
		.replace(/\s+/g, '') // hapus spasi
		.replace(/[^a-z0-9]/g, '');

export const formatPoint = (value) => {
	return new Intl.NumberFormat('id-ID').format(value);
};

export const generateAccessCode = (companyId, brandId, outletId, env) => {
  const random = Math.random().toString(36).substring(2, 7).toUpperCase();
  let raw = `${companyId}-${brandId}-${outletId}-${env}-${random}`;
	if(env === 'production') {
		raw = `${companyId}-${brandId}-${outletId}-${random}`;
	}
  return encodeValue(raw);
};

export const getCssVar = (name, fallback = null) => {
  if (typeof window === 'undefined') return fallback;

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();

  // kondisi dianggap "tidak ada"
  const isEmpty = isEmptyData(value)

  return isEmpty ? fallback : value;
};

export const getJwtExpiration = (token) => {
  if (!token) return null;
  
  const payload = token.split('.')[1];

  const json = JSON.parse(
    atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
  );

  return json?.exp;
}

// Haversine Formula
export const getDistanceInMeters = (
  lat1,
  lng1,
  lat2,
  lng2,
) => {
	if ([lat1, lng1, lat2, lng2].some((v) => typeof v !== 'number' || Number.isNaN(v))) {
		return 0;
	}
  const R = 6371000;

  const toRad = (deg) => deg * Math.PI / 180;

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) ** 2;

  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const roundCoordinate = (value, precision = 5) => {
  return Number(Number(value).toFixed(precision));
};

export const buildSafeLink = (link) => {
  if (!link) return '#';

  try {
    const [path, queryString] = link.split('?');
    const params = new URLSearchParams(queryString);

    const tag = params.get('tag');

    if (tag) {
      params.set('tag', encodeURIComponent(tag));
    }

    return `${path}?${params.toString()}`;
  } catch {
    return link;
  }
};

export const loggingGps = (error, location = null, isLoadedGoogleMaps = null, permission) => {
  return {
    code: error?.code ?? 'UNKNOWN',
    message: error?.message ?? 'TIMEOUT',
    isLoadedGoogleMaps,
    permission: permission,
    coords: location ?? null,
    deviceInfo: navigator.userAgent,
    platformInfo: detectPlatformInfo(navigator.userAgent),
    timestamp: new Date().toISOString(),
  };
};

export const normalizePermission = (value) => {
  switch (value) {
    case GEOLOCATION_PERMISSION.GRANTED:
    case GEOLOCATION_PERMISSION.DENIED:
    case GEOLOCATION_PERMISSION.PROMPT:
      return value;
    default:
      return GEOLOCATION_PERMISSION.UNKNOWN;
  }
};

export const askUserPermission = () => {
  return new Promise((resolve) => {
    let done = false;

    const timeout = setTimeout(() => {
      if (!done) {
        done = true;
        resolve(GEOLOCATION_PERMISSION.UNKNOWN);
      }
    }, GEOLOCATION_ASK_TIMEOUT);

    navigator.geolocation.getCurrentPosition(
      () => {
        if (done) return;
        done = true;
        clearTimeout(timeout);
        resolve(GEOLOCATION_PERMISSION.GRANTED);
      },
      (error) => {
        if (done) return;
        done = true;
        clearTimeout(timeout);

        if (error.code === error.PERMISSION_DENIED) {
          resolve(GEOLOCATION_PERMISSION.DENIED);
        } else {
          resolve(GEOLOCATION_PERMISSION.UNKNOWN);
        }
      },
    );
  });
};

const isBrowser = typeof window !== 'undefined';

const getBaseContext = () => {
	if (!isBrowser) return {};

	let isIframe = false;
	try {
		isIframe = window !== window.top;
	} catch {
		isIframe = true; // kalau akses window.top aja udah throw, berarti pasti cross-origin iframe
	}

	return {
		currentUrl: window.location?.href,
		referrer: document?.referrer || null,
		userAgent: navigator?.userAgent,
		isIframe,
		timestamp: new Date().toISOString(),
	};
};

export const logError = (error, extraContext = {}) => {
	return;
	// const err = error instanceof Error ? error : new Error(String(error));
	// const context = { ...getBaseContext(), ...extraContext };

	// console.error(err.message, context);

	// setTimeout(() => {
	// 	try {
	// 		if (window.newrelic?.noticeError) {
	// 			window.newrelic.noticeError(err, context);
	// 		}
	// 	} catch (loggingError) {
	// 		console.info('Failed to send error to New Relic', loggingError);
	// 	}
	// }, 0);
};

const CHANNEL_NAME = 'UFoodChannel';

export const isInFlutterApp = () =>
  typeof window !== 'undefined' && !!window[CHANNEL_NAME];

export const postToFlutter = (payload) => {
  if (!isInFlutterApp()) return false;

  window[CHANNEL_NAME].postMessage(
    JSON.stringify({
      ...payload,
      requestId: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    }),
  );

  return true;
};

export const normalizePath = (path = '/') => {
  if (typeof path !== 'string') return path;
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return path;
  return '/' + path.replace(/^\/+/, '');
};